import { Link } from '@tanstack/react-router';
import { ChevronRight, Copy, WifiOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { getRuntimeConfig } from '@nucleus-commerce/admin-kit/config';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@nucleus-commerce/ui/components/collapsible';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import type { TryItBody, TryItResponse } from '../../api/try-it-client';

import { copyText } from './copy-text';

/** Headers worth a line of their own above the body (all of them sit in the list below). */
const HIGHLIGHTED = ['x-request-id', 'x-ratelimit-remaining', 'idempotency-replayed'];

function statusTone(status: number) {
  if (status >= 500) return 'bg-destructive/10 text-destructive border-destructive/25';
  if (status >= 400)
    return 'bg-warning/15 text-warning-foreground border-warning/40 dark:text-warning';
  if (status >= 300) return 'bg-info/10 text-info border-info/25';
  return 'bg-success/10 text-success border-success/25';
}

function bodyText(body: TryItBody): string | null {
  if (body.kind === 'empty') return null;
  return body.kind === 'json' ? JSON.stringify(body.value, null, 2) : body.value;
}

/** The error envelope's `code`, when the body is one (`{ error: { code, message } }`). */
function errorCode(body: TryItBody): string | null {
  if (body.kind !== 'json' || typeof body.value !== 'object' || body.value === null) return null;
  const error = (body.value as { error?: unknown }).error;
  if (typeof error !== 'object' || error === null) return null;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

/** What came back: status, time, the headers CORS lets the page read, and the body. */
export function ResponseView({
  response,
  failed,
}: {
  response: TryItResponse | null;
  /** The request got no response at all (network down, CORS refused). */
  failed: boolean;
}) {
  const { t } = useTranslation('docs');

  if (failed) {
    return (
      <Alert variant="destructive" role="alert">
        <WifiOff aria-hidden />
        <AlertTitle>{t('tryIt.response.networkTitle')}</AlertTitle>
        <AlertDescription>
          {t('tryIt.response.network', {
            origin: window.location.origin,
            baseUrl: getRuntimeConfig().apiBaseUrl,
          })}
        </AlertDescription>
      </Alert>
    );
  }
  if (!response) return null;

  const text = bodyText(response.body);
  const code = errorCode(response.body);
  const headers = new Map(response.headers);
  const limit = headers.get('x-ratelimit-limit');

  return (
    <section aria-label={t('tryIt.response.title')} className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span
          data-testid="try-it-status"
          className={cn(
            'inline-flex h-6 items-center rounded border px-2 font-mono text-xs font-semibold',
            statusTone(response.status),
          )}
        >
          {response.status}
        </span>
        <span className="text-muted-foreground">{response.statusText}</span>
        <span className="text-muted-foreground ml-auto text-xs tabular-nums">
          {t('tryIt.response.time', { ms: response.durationMs })}
        </span>
      </div>

      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
        {HIGHLIGHTED.filter((name) => headers.has(name)).map((name) => (
          <div key={name} className="contents">
            <dt className="text-muted-foreground font-mono">{name}</dt>
            <dd className="font-mono break-all">
              {name === 'x-ratelimit-remaining' && limit
                ? `${headers.get(name)} / ${limit}`
                : headers.get(name)}
            </dd>
          </div>
        ))}
      </dl>

      {code ? (
        <p className="text-xs">
          {t('tryIt.response.errorCode')}{' '}
          <Link
            to="/docs/guides/$slug"
            params={{ slug: 'conventions' }}
            hash="error-codes"
            className="font-mono font-medium underline underline-offset-4"
          >
            {code}
          </Link>
        </p>
      ) : null}

      {text === null ? (
        <p className="text-muted-foreground text-sm">{t('tryIt.response.noBody')}</p>
      ) : (
        <div className="relative">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="bg-background/80 absolute top-1.5 right-1.5 size-7"
            aria-label={t('tryIt.response.copy')}
            onClick={() => void copyText(text, t)}
          >
            <Copy aria-hidden />
          </Button>
          <pre
            data-testid="try-it-body"
            className="bg-muted max-h-[28rem] overflow-auto rounded-md p-3 font-mono text-[12px] leading-relaxed"
          >
            {text}
          </pre>
        </div>
      )}

      <Collapsible>
        <CollapsibleTrigger className="group text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs font-medium">
          <ChevronRight
            className="size-3.5 transition-transform group-data-[state=open]:rotate-90"
            aria-hidden
          />
          {t('tryIt.response.headers', { count: response.headers.length })}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
            {response.headers.map(([name, value]) => (
              <div key={name} className="contents">
                <dt className="text-muted-foreground font-mono">{name}</dt>
                <dd className="font-mono break-all">{value}</dd>
              </div>
            ))}
          </dl>
          <p className="text-muted-foreground mt-2 text-xs">{t('tryIt.response.headersHint')}</p>
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
}
